/**
 * Guide texts (EN = source language).
 * Markup inside strings: [ui]App button[/ui], [watch]Text on the watch[/watch],
 * [set]Setting label[/set], [b]bold[/b], [code]code[/code], [link=/path]text[/link].
 * Arrays are rendered as steps or lists. Keep markup and array lengths when translating.
 */
const guides = {
    "ui": {
        "note": {
            "info": "Note",
            "warn": "Important",
            "tip": "Tip",
            "watch": "Shown on the watch"
        },
        "breadcrumbs": "Breadcrumb",
        "copy": "Copy",
        "copied": "Copied",
        "menu": "Menu",
        "language": "Language"
    },
    "g": {
        "dataFields": {
            "lead": "All options you can choose for a data field, with what each one shows. Which ones your watch offers depends on the watchface and the watch model.",
            "availLabel": "Not on every watch",
            "availNote": "Not every option is available on every watch. Older or smaller watches have a shorter list, and some values need a sensor, an app or a key. Options marked below need a newer watch, a MIP display or exist in one watchface only. The list in your watchface settings is what counts.",
            "search": "Search data fields, e.g. tide, sunset, steps",
            "avail": {
                "newer": "Newer watches (CIQ 4.2+)",
                "newest": "Newest watches only",
                "mip": "MIP displays only",
                "blackhawk": "Black Hawk only"
            }
        },
        "touch": {
            "lead": "On touchscreen watches with Connect IQ 4.2 or newer you control the watchface with a long press: run an action on the time or the icons, or open a Garmin app from a data field.",
            "actions": {
                "title": "Long-press actions",
                "text": "Set the actions in the watchface settings: [set]Hour Long Press Action[/set], [set]Minute Long Press Action[/set], [set][middle area] Left Icons Long Press Action[/set] and [set][middle area] Right Icons Long Press Action[/set]. The face confirms the change with a short message such as [watch]AOD off[/watch].",
                "optionsTitle": "Actions",
                "options": [
                    {
                        "term": "None",
                        "text": "The long press does nothing."
                    },
                    {
                        "term": "turn off AOD",
                        "text": "The screen stays blank in low-power mode and AOD. Long-press again to return to your idle setting."
                    },
                    {
                        "term": "turn off display (long press anywhere to activate again - touchscreen stays active)",
                        "text": "Blanks the whole face. A long press anywhere on the screen turns it back on."
                    },
                    {
                        "term": "Toggle Top Switchview",
                        "text": "Switches the top area between view 1 and view 2."
                    },
                    {
                        "term": "Toggle Bottom Switchview",
                        "text": "Switches the bottom area between view 1 and view 2."
                    },
                    {
                        "term": "Toggle Switchviews",
                        "text": "Tactical Elite only. Steps to the next of up to four switchviews."
                    },
                    {
                        "term": "Switch datafield set",
                        "text": "Fletcher and Night Fever only. Swaps the main and the alternate data field set."
                    }
                ],
                "slotsTitle": "Which watchface offers what",
                "slots": [
                    {
                        "term": "Senator, Tomahawk, Vanguard",
                        "text": "Hour, minute, left and right icons. By default the hour switches the top view and the minute the bottom view."
                    },
                    {
                        "term": "Black Hawk, Mission Zero",
                        "text": "Hour, minute and left icons. Same defaults as Vanguard."
                    },
                    {
                        "term": "Tactical Elite",
                        "text": "Hour, minute, left and right icons. All start with None."
                    },
                    {
                        "term": "Basic Pro",
                        "text": "Hour, minute, left and right icons. Only None, turn off AOD and turn off display."
                    },
                    {
                        "term": "Fletcher, Night Fever",
                        "text": "Hour and minute. Night Fever switches the data field set by default, Fletcher starts with None."
                    },
                    {
                        "term": "Nevada Pro",
                        "text": "Hour and minute. Only None, turn off AOD and turn off display."
                    },
                    {
                        "term": "Io Zen",
                        "text": "No long-press actions."
                    }
                ]
            },
            "openApps": {
                "title": "Open Garmin apps",
                "text": "With [set]activate Opening Apps on Long Pressing on screen[/set] switched on (the default), a long press on a data field opens the matching Garmin app or widget, for example heart rate, steps or weather. A long press on a chart or a weather view opens the related widget. A long press on the time runs the long-press action instead."
            }
        },
        "displayModes": {
            "lead": "What the watchface shows while the watch is idle, and how much power it uses, depends on the display type and a few settings.",
            "idle": {
                "title": "What stays visible when idle",
                "text": "[set]All day @Idle Mode Options[/set] sets what stays visible in low-power mode or AOD: all information, the time only, a blank screen or an analog clock. Some faces add variants of the middle section.\n\n[set]Nighttime @Idle Mode Options (overwrites all day)[/set] applies between the sleep and wake times of your Garmin user profile."
            },
            "aod": {
                "title": "Always-on on AMOLED watches",
                "text": "[set]Always On (AOD) Options[/set] sets the always-on style. All patterns protect the display against burn-in.",
                "options": [
                    {
                        "term": "No AOD",
                        "text": "The screen turns off when the watch is inactive."
                    },
                    {
                        "term": "AOD check pattern: 50% brightness (burn in protected)",
                        "text": "Shows the idle content through a check pattern."
                    },
                    {
                        "term": "AOD diagonal lines: 70% brightness (burn in protected)",
                        "text": "Shows the idle content through diagonal lines."
                    },
                    {
                        "term": "AOD horizontal lines: 50% brightness (burn in protected)",
                        "text": "Shows the idle content through horizontal lines."
                    },
                    {
                        "term": "AOD 100% brightness - middle section only - dimmed by system",
                        "text": "Shows only the middle section at full brightness. The watch dims it."
                    }
                ],
                "fixed": "Night Fever and Io Zen have no AOD setting. On AMOLED watches they use a fixed, burn-in protected pattern."
            },
            "mip": {
                "title": "Power saving on MIP watches",
                "text": "[set]Operating mode[/set] sets how much of the screen is redrawn. [b]Normal Mode[/b] redraws the full screen. The power saving modes redraw less and save battery. Power Saving Mode 2 saves the most but can flicker on some devices.\n\nBasic Pro, Nevada Pro, Senator, Fletcher, Vanguard, Black Hawk and Mission Zero have this setting."
            },
            "hands": {
                "title": "Hands on the Instinct Crossover AMOLED",
                "text": "The Instinct Crossover AMOLED has physical hands. With [set]Activate hands move feature after rasing your wrist[/set] switched on, the hands move to a parked position a few seconds after you raise your wrist, so they do not cover the display. They return to the time when the watch goes back to low-power mode.\n\nThis applies to Nevada Pro, Senator, Tomahawk, Fletcher and Night Fever. It is on by default in Nevada Pro and Tomahawk. Senator names the setting [set]Activate the movement of the analog hands after raising your wrist.[/set]"
            }
        },
        "watchMenu": {
            "lead": "Newer watches (Connect IQ 4.2 and later) have a small menu for the watchface on the watch itself. All other settings stay in the Connect IQ app.",
            "which": "Basic Pro, Nevada Pro, Tactical Elite, Senator, Tomahawk, Fletcher, Io Zen, Vanguard, Night Fever, Black Hawk, Mission Zero and Firmament have this menu.",
            "open": {
                "title": "Open the menu",
                "steps": [
                    "Open the settings of the active watchface on the watch, for example from the watch face selection.",
                    "The menu shows [watch]Style[/watch], [watch]Info[/watch] and a hint that all other settings are in the Connect IQ app."
                ]
            },
            "style": {
                "title": "Style: theme and fonts",
                "steps": [
                    "Open [watch]Style[/watch] and pick [watch]Themes[/watch], [watch]TimeFont[/watch] or [watch]Datafont[/watch].",
                    "Browse with the Up and Down buttons or tap the left or right half of the screen. A live preview shows each option with its position, for example 3 / 8.",
                    "Press Start or tap the confirm button to apply. Press Back to leave without changes."
                ]
            },
            "info": {
                "title": "Info",
                "text": "[watch]Info[/watch] shows the version, the WatchfaceID, the lock status and whether a position is stored."
            }
        },
        "devices": {
            "lead": "Garmin watches differ in display, touch and graphics power. That is why some features look different or are missing on some models. Each watchface page lists its own device notes in the overview.",
            "display": {
                "title": "MIP and AMOLED",
                "text": "MIP displays are always on and use little power. The watchfaces offer power-saving modes there. AMOLED displays are brighter and dim or turn off when idle. There the watchfaces offer always-on styles with burn-in protection. [link=/guides/display-modes]Always-on & power saving[/link]"
            },
            "touch": {
                "title": "Touch",
                "text": "Long-press actions and opening apps by long press need a touchscreen and Connect IQ 4.2 or newer. [link=/guides/touch]Long press & touch[/link]"
            },
            "older": {
                "title": "Older watches",
                "text": "Some features need newer hardware. They are missing on the fenix 5 and fenix 6 series, Forerunner 245/645/745/945, vivoactive 3/4, Venu (1st gen), Venu Sq, Forerunner 55, MARQ (1st gen) and similar models. Instinct 2 and Instinct 3 with MIP display use their own reduced layout.",
                "options": [
                    {
                        "term": "Touch and menu",
                        "text": "Long-press actions, opening apps by long press and the menu on the watch."
                    },
                    {
                        "term": "Switchviews",
                        "text": "The second wrist gesture setting, view 2 in Senator, Tomahawk and Vanguard, and Switchview 3 and 4 in Tactical Elite."
                    },
                    {
                        "term": "Views",
                        "text": "Text forecast, Tactical Position, World Map + mgrs coordinate and the 7-day charts. Tide Chart and World Map + coordinates stay available on fenix 6 class devices."
                    },
                    {
                        "term": "Data fields",
                        "text": "Glucose (CGM Connect Widget), Air Quality Index (Atmos Air Quality), Training Status, activity time fields, Next Event Time, Custom Text and Days to date. Tide data fields are missing on fenix 5 class devices."
                    },
                    {
                        "term": "Design",
                        "text": "Analog clock styles, background images, gradient colors and custom colors are reduced or missing, depending on the watchface."
                    }
                ]
            }
        },
        "common": {
            "figStyleMenu": "Style on the watch: theme and fonts with a live preview",
            "figNoData": "Vanguard without weather data: “waiting for data”",
            "figGps": "No stored position: “GPS?” instead of the place name",
            "figInfo": "The Info page on the watch: WatchfaceID, Lock Status and Position",
            "figWatchMenu": "The watchface’s own menu on the watch with Style and Info",
            "figView1": "View 1: data fields in the lower area",
            "figView2": "View 2: the same area with the forecast chart",
            "figTrialEnded": "Vanguard VX after the trial: “trial time has ended” in a switchable area",
            "figWeatherDetail": "Weather detail view in Vega with weather station and provider",
            "figTidesStation": "Tide view in Vega: station, distance and the next low and high tide",
            "figSwitchTouch": "Black Hawk: tap the marked spot to switch the upper or lower area to its second view",
            "onThisPage": "On this page",
            "appliesTo": "Applies to",
            "allWatchfaces": "all watchfaces",
            "troubleshooting": "Troubleshooting"
        },
        "install": {
            "lead": "Watchfaces and apps are installed with the Garmin [b]Connect IQ[/b] app on your phone. Then you select the watchface on the watch.",
            "install": {
                "title": "Install with the Connect IQ app",
                "steps": [
                    "Connect the watch to your phone via Bluetooth and open the [b]Connect IQ[/b] app. The Garmin Connect app is not the right one for this.",
                    "Open the watchface in the store: search for its name, or use [ui]Go to IQ Store[/ui] on its page on this website.",
                    "Tap [ui]Install[/ui]. The watchface goes into the download queue and is transferred to the watch.",
                    "When the transfer is finished, the app shows a message and the watchface is on the watch."
                ],
                "noteScreen": "Keep the phone screen on and the Connect IQ app open until the installation is finished. This also applies to updates."
            },
            "select": {
                "title": "Select the watchface on the watch",
                "intro": "If the watchface is installed but not shown, select it on the watch:",
                "steps": [
                    "Hold [ui]MENU[/ui]. On many touch watches you can also press and hold the watch face.",
                    "Open [ui]Watch Face[/ui]. On some models it is called [ui]Watch Faces[/ui] or [ui]Display designs[/ui].",
                    "Scroll to the watchface with UP and DOWN or by swiping.",
                    "Confirm with START or a tap. The watchface is now active."
                ],
                "settingsHint": "The watchface settings can only be opened in the Connect IQ app once the watchface is installed. [link=/guides/settings]How to open the settings[/link]"
            },
            "trouble": {
                "queue": {
                    "q": "The installation is stuck in the queue or fails",
                    "a": "This is caused by the Connect IQ app or Garmin’s servers, not by the watchface.\n\nRestart the watch, close the Connect IQ app completely and try again. Keep the phone screen on during the transfer. If it still fails, try again later. These problems usually disappear on their own."
                },
                "notShown": {
                    "q": "The watchface is installed but I cannot see it",
                    "a": "It is probably not selected yet. Select it in the watch face menu as described above. If it is missing there too, install it again with the Connect IQ app."
                },
                "garminPay": {
                    "q": "A Garmin Pay purchase is stuck at “in progress”",
                    "a": "Elite purchases with Garmin Pay are processed entirely by Garmin. Only Garmin support can help with this."
                }
            }
        },
        "settings": {
            "lead": "All watchface settings are changed in the Connect IQ app on your phone. This page shows how, explains every data field option and helps when something does not work.",
            "tabs": {
                "change": "Change settings",
                "options": "Data field options",
                "trouble": "Troubleshooting"
            },
            "change": {
                "title": "Change settings",
                "steps": [
                    "Connect the watch to your phone via Bluetooth and open the [b]Connect IQ[/b] app (not Garmin Connect).",
                    "Open the watchface: in the [ui]Device[/ui] section under [ui]My watch faces[/ui], or with the search.",
                    "Tap [ui]Settings[/ui].",
                    "Change the options. Data fields, colors and advanced options are all in this list.",
                    "Tap [ui]Save[/ui] at the top right. The settings are sent to the watch and the list closes."
                ],
                "expressTitle": "On a computer with Garmin Express",
                "express": "Connect the watch, open the list of installed watchfaces and click the three dots next to the watchface. Some Garmin Express versions have problems with settings, so the Connect IQ app is the safer choice."
            },
            "options": {
                "linkTitle": "All data field options",
                "linkText": "Over 100 values for the data fields, from heart rate and tides to golden hour, with what each one shows and on which watches it is available.",
                "intro": "This list explains all data field options. Not every option exists in every watchface or on every watch, and names can differ slightly.",
                "pro": "Options marked with ♛ are PRO options. They work during the trial and after unlocking."
            },
            "trouble": {
                "cantSave": {
                    "q": "The settings cannot be saved and an error appears",
                    "a": "Garmin’s servers sometimes reject certain characters. Clear the theme string field (if there is one) and save again."
                },
                "wontOpen": {
                    "q": "The settings do not open or load slowly",
                    "a": "The settings are stored on Garmin’s servers. If they are slow, wait a little and try again.\n\nClose the Connect IQ app completely and restart the watch if needed. If you only see [ui]Install[/ui] instead of [ui]Settings[/ui], the watchface is not installed on this watch yet."
                },
                "missing": {
                    "q": "I cannot find an option",
                    "a": "The available options depend on the watch model and the watchface. Some options only exist on newer watches or only on AMOLED or MIP displays."
                },
                "na": {
                    "q": "A data field only shows [watch]n/a[/watch]",
                    "a": "Your watch does not provide this value. Usually the sensor is missing (for example barometer or pulse oximeter) or the model is older. Choose another option for this field."
                },
                "key": {
                    "q": "A data field shows [watch]key?[/watch]",
                    "a": "This is a PRO option and the trial has ended. [link=/guides/purchase]Unlock the watchface[/link] or choose another option."
                },
                "units": {
                    "q": "How do I change units and the 12/24-hour format?",
                    "a": "Temperature, distance, height and the time format follow the system settings of the watch. Hold [ui]MENU[/ui] and open [ui]System[/ui] › [ui]Format[/ui].\n\nPressure and wind speed are set in the watchface settings with [set]Pressure Unit[/set] and [set]Windspeed Unit[/set]."
                }
            }
        },
        "glossary": {
            "lead": "Short explanations of the terms used in the watchface settings and on this website.",
            "terms": [
                {
                    "term": "WatchfaceID",
                    "text": "A 5- or 6-digit number in the first line of the watchface settings. The watch creates it the first time the watchface runs, and it is different on every watch. You need it to buy an unlock key. [link=/guides/find-watchface-id]Find your WatchfaceID[/link]"
                },
                {
                    "term": "ProKey",
                    "text": "The unlock key you receive by email after buying on this website. Enter it in the ProKey field directly below the WatchfaceID. While the field is empty, it shows the remaining trial days."
                },
                {
                    "term": "VX",
                    "text": "The version of a watchface that runs on all supported watches. It includes a 5-day trial and is unlocked with a ProKey from this website. The key is tied to one watch."
                },
                {
                    "term": "Elite",
                    "text": "A separate version of the same watchface, bought in the Connect IQ Store with Garmin Pay. It is tied to your Garmin account and needs no key. It is only available on watches that support Garmin Pay."
                },
                {
                    "term": "♛ / PRO",
                    "text": "Marks options of the paid version. They work during the trial. Afterwards they stop working or show [watch]key?[/watch] until you unlock the watchface. Watchfaces with a VX and an Elite version show a notice on the display instead."
                },
                {
                    "term": "Theme string",
                    "text": "A short code in the watchface settings that stores all color settings. Copy it to save or share a color setup. It only works in the same watchface. [link=/guides/themes]Themes & theme strings[/link]"
                },
                {
                    "term": "Switchview",
                    "text": "An area of the watchface that can show a second view. You switch it with a second wrist movement or, on touch watches, with a long press. [link=/guides/switchviews]Switchviews & gestures[/link]"
                },
                {
                    "term": "GPS fix",
                    "text": "The moment the watch receives its position via GPS. Watchfaces need a position for sunrise, sunset, moonrise, weather and tides. [link=/guides/location]GPS & location[/link]"
                },
                {
                    "term": "Low-power mode",
                    "text": "About 10 seconds after you look at the watch, it switches to an energy-saving display. On MIP watches the seconds usually disappear and the screen only updates once a minute."
                },
                {
                    "term": "AOD",
                    "text": "Always-on display of AMOLED watches. It shows a reduced, dimmed version of the watchface while the watch is idle."
                },
                {
                    "term": "MIP / AMOLED",
                    "text": "The two display types of Garmin watches. MIP displays (for example Instinct, fenix 7, Forerunner 955) are always on and easy to read in sunlight. AMOLED displays (for example epix, Venu, fenix 8 AMOLED) are brighter and more colorful but dim or turn off when idle. Some options only exist for one of the two."
                },
                {
                    "term": "API key",
                    "text": "A personal access key for an online service such as OpenWeatherMap, Visual Crossing or Stormglass. You create it for free on the provider’s website and paste it into the watchface settings."
                },
                {
                    "term": "UTC offset",
                    "text": "The difference between a time zone and UTC, entered in minutes. Used for the second and third time zone. [link=/guides/timezones]Time zones[/link]"
                }
            ]
        },
        "purchase": {
            "lead": "Most watchfaces exist in two versions: VX and Elite. They have the same features and only differ in how you buy and unlock them.",
            "tabs": {
                "versions": "VX or Elite",
                "buy": "Buy a key",
                "trouble": "Troubleshooting"
            },
            "versions": {
                "afterTrialLabel": "After the trial",
                "title": "VX or Elite?",
                "same": "Both versions have the same features. They are two separate entries in the store, so a VX key does not unlock the Elite version and the other way round.",
                "vxTitle": "VX version",
                "vx": [
                    "Runs on all supported watches.",
                    "Includes a free 5-day trial.",
                    "After the trial you unlock it with a ProKey bought on this website.",
                    "The key is tied to one watch through its WatchfaceID."
                ],
                "eliteTitle": "Elite version",
                "elite": [
                    "Bought directly in the Connect IQ Store with Garmin Pay.",
                    "Tied to your Garmin account, so you can install it on a new watch without paying again.",
                    "Needs no key, and no key is sent.",
                    "Garmin Pay is not available on every watch or in every country."
                ],
                "afterTrial": "What happens after the trial depends on the watchface. Watchfaces with a VX and an Elite version show a notice on the watch face that the trial has ended. Depending on the watchface it is small or covers the whole display. All other watchfaces show no notice: only the PRO options (♛) stop working or show [watch]key?[/watch]. Everything else keeps working, and you can replace PRO options with free ones."
            },
            "buy": {
                "title": "Buy a key on this website",
                "steps": [
                    "Install the VX version and select it as your watch face. The WatchfaceID is only created once the watchface has run on the watch.",
                    "Open the watchface settings in the Connect IQ app and copy the WatchfaceID from the first line. [link=/guides/find-watchface-id]Where to find it[/link]",
                    "Open the watchface on this website, enter the WatchfaceID in the payment form and pay.",
                    "You receive the ProKey by email. Enter it in the ProKey field below the WatchfaceID and tap [ui]Save[/ui]."
                ],
                "noInstall": "Buying a key does not install the watchface. Install it first with the Connect IQ app. [link=/guides/install]Installation guide[/link]",
                "paddle": "Payments are processed by Paddle. You do not need an account."
            },
            "trouble": {
                "noKey": {
                    "q": "I paid but did not receive a key",
                    "a": "Check your spam folder first. Key emails often end up there.\n\nIf it is not there, the email address may contain a typo. Write via the [link=/contact]contact form[/link] and include the email address you used and your WatchfaceID."
                },
                "notAccepted": {
                    "q": "The key is not accepted",
                    "items": [
                        "The key only works for the watchface and the WatchfaceID it was bought for.",
                        "A VX key does not unlock the Elite version or another watchface.",
                        "Tap [ui]Save[/ui] after entering the key and wait until the settings have been sent to the watch.",
                        "Newer watchfaces show the [b]Lock Status[/b] on the watch. [link=/guides/find-watchface-id]Check it on the Info page[/link]"
                    ]
                },
                "newWatch": {
                    "q": "I have a new watch and my old key does not work",
                    "a": "A VX key is tied to one watch. The new watch has a different WatchfaceID. If Garmin replaced your watch under warranty, write via the [link=/contact]contact form[/link] and include the old and the new WatchfaceID."
                },
                "permanent": {
                    "q": "Is this a subscription?",
                    "a": "No. A key is a one-time purchase and stays valid as long as you use the same watch."
                },
                "doubleCharge": {
                    "q": "I was charged twice",
                    "a": "Write via the [link=/contact]contact form[/link]. The duplicate payment is refunded. It can take a few days until the money is back on your account."
                },
                "garminPay": {
                    "q": "A Garmin Pay purchase is stuck at “in progress”",
                    "a": "Elite purchases are processed entirely by Garmin. Only Garmin support can help with this."
                }
            }
        },
        "findId": {
            "lead": "To buy a key you need the WatchfaceID. It is in the watchface settings and, on newer watchfaces, also on the watch.",
            "what": {
                "title": "What is the WatchfaceID?",
                "text": "A 5- or 6-digit number that identifies the watchface on your watch. It is different on every watch. The watch creates it the first time the watchface runs."
            },
            "app": {
                "title": "In the Connect IQ app",
                "steps": [
                    "Make sure the watchface is installed and selected as your watch face.",
                    "Open the [b]Connect IQ[/b] app and go to the watchface: [ui]Device[/ui] › [ui]My watch faces[/ui], or use the search.",
                    "Tap [ui]Settings[/ui].",
                    "The WatchfaceID is in the first line. Copy it or write it down."
                ]
            },
            "key": {
                "title": "Enter the key",
                "text": "The ProKey field is directly below the WatchfaceID. While it is empty, it shows the remaining trial days. Enter your key there and tap [ui]Save[/ui].",
                "label": "The field is called:",
                "labelValue": "Enter ProKey here: (shows remaining trial days when empty)"
            },
            "watch": {
                "title": "On the watch",
                "text": "Basic Pro, Nevada Pro, Tactical Elite, Senator, Tomahawk, Fletcher, Io Zen, Vanguard, Night Fever, Black Hawk, Mission Zero and Firmament have their own menu on newer watches (Connect IQ 4.2 and later). The [ui]Info[/ui] page shows the WatchfaceID and whether the watchface is unlocked.",
                "steps": [
                    "Open the settings of the active watchface on the watch, for example from the watch face selection.",
                    "Select [ui]Info[/ui]."
                ],
                "rows": [
                    {
                        "term": "Watchface ID",
                        "text": "Your WatchfaceID."
                    },
                    {
                        "term": "Lock Status",
                        "text": "[watch]Unlocked[/watch] or [watch]Locked[/watch]."
                    },
                    {
                        "term": "Position",
                        "text": "[watch]Valid[/watch] when the watchface has a position for weather, sun times and tides."
                    }
                ]
            },
            "express": {
                "title": "With Garmin Express on a computer",
                "steps": [
                    "Install Garmin Express and connect the watch.",
                    "Open the list of installed watchfaces.",
                    "Click the three dots next to the watchface and open the settings."
                ],
                "note": "Some Garmin Express versions have problems with settings. The Connect IQ app is more reliable."
            },
            "zero": {
                "title": "The WatchfaceID shows 0",
                "text": "The watch has not created the ID yet. Select the watchface on the watch, wait a minute and open the settings again. Restarting the watch can help.",
                "warn": "Do not buy a key for the ID 0. It will not work. If the ID stays 0, write via the [link=/contact]contact form[/link] first."
            }
        },
        "weather": {
            "forecastTitle": "Forecast chart",
            "forecastText": "The switchview [b](Chart) Weather hourly forecast[/b] draws two forecast lines over a background chart. Choose the values with [set]Forecast Option 1 (Line)[/set], [set]Forecast Option 2 (Line)[/set] and [set]Forecast Option 3 (Background Chart)[/set]: Temperature, Windspeed, Weather Precipitation, UV Index, Cloud coverage in % or Weather Humidity. The colors are set with [set]Color Weather Forecast Line 1[/set], [set]Color Weather Forecast Line 2[/set] and [set]Color Chart (Forecast / Chart Views)[/set].\n\n[b](Text) Weather hourly forecast[/b] shows the same values as text and needs a newer device. Forecast views are part of Tactical Elite, Senator, Tomahawk, Vanguard, Black Hawk and Mission Zero.",
            "noDataTitle": "Shows “no data”?",
            "pickTitle": "Which provider do you use?",
            "noKey": "No API key",
            "freeKey": "Free API key",
            "min20": "Every 20 min at most",
            "newerOnly": "Newer watchfaces",
            "isDefault": "Default",
            "lead": "Many watchfaces let you choose where the weather data comes from. You change the provider in the watchface settings. The default is Garmin weather.",
            "noData": "If a weather field shows [watch]no data[/watch] or [watch]waiting for data[/watch], the watch has not received weather data yet. Follow the steps for your provider below.",
            "settingsTitle": "The settings",
            "settings": [
                {
                    "term": "Weather Provider",
                    "text": "Garmin, OpenWeatherMap, Visual Crossing, Yr or Open-Meteo. Which ones are offered depends on the watchface."
                },
                {
                    "term": "Weather ApiKey",
                    "text": "Only needed for OpenWeatherMap and Visual Crossing. One field for both."
                },
                {
                    "term": "Weather refresh rate in minutes",
                    "text": "5 to 60 minutes, default 20. Yr needs at least 20 minutes. Garmin weather ignores this setting."
                }
            ],
            "requirementsTitle": "All providers need",
            "requirements": [
                "The watch is connected to the phone via Bluetooth.",
                "The [b]Garmin Connect[/b] app (not Connect IQ) runs at least in the background. The watch uses it to go online.",
                "A position. If the watch shows [watch]GPS?[/watch], [link=/guides/location]do a GPS fix[/link]."
            ],
            "tabs": {
                "garmin": "Garmin",
                "yr": "Yr",
                "openmeteo": "Open-Meteo",
                "owm": "OpenWeatherMap",
                "vc": "Visual Crossing"
            },
            "garmin": {
                "title": "Garmin weather",
                "text": "Garmin weather needs no API key. The Garmin Connect app must have access to the phone’s location, and location services must be on.\n\nThe watch downloads the data itself, which can take a few minutes.",
                "check": "Check the Garmin weather widget on the watch. If it shows no data, the watchface cannot show any either, because both use the same source.",
                "location": "The place name shown with Garmin weather is the nearest weather station, often an airport. For a more precise location, use another provider.",
                "missingTitle": "Not included in Garmin weather",
                "missing": [
                    "Air quality",
                    "UV index",
                    "Weather description"
                ]
            },
            "yr": {
                "title": "Yr",
                "text": "Yr is run by the Norwegian Meteorological Institute and NRK. It needs no API key. Select [b]Yr[/b] as provider.",
                "interval": "Yr is updated at most every 20 minutes, even if you enter a lower refresh rate.",
                "wrongPlace": "If no data loads or the place is wrong, update the stored position with a [link=/guides/location]GPS fix[/link]. The data updates within a few minutes.",
                "missingTitle": "Not included in Yr",
                "missing": [
                    "Air quality"
                ]
            },
            "openmeteo": {
                "title": "Open-Meteo",
                "text": "Open-Meteo needs no API key. Select [b]Open-Meteo[/b] as provider. It is offered by the newer watchfaces such as Vanguard, Senator, Tactical Elite, Tomahawk, Black Hawk, Mission Zero and Zenith."
            },
            "owm": {
                "title": "OpenWeatherMap",
                "steps": [
                    "Create a free account at [link=https://openweathermap.org/api]openweathermap.org[/link] and copy your API key.",
                    "Paste it into [set]Weather ApiKey[/set] and select [b]OpenWeatherMap[/b] as provider.",
                    "Save. A new key can take a few hours until it works."
                ],
                "uvTitle": "UV index and precipitation",
                "uv": "Keys created before about February 2023 include these values. Newer keys need the free “One Call by Call” plan for them. The first 1000 requests per day are free. Set the limit to 1000, which is more than enough for any watchface."
            },
            "vc": {
                "title": "Visual Crossing",
                "steps": [
                    "Create a free account at [link=https://www.visualcrossing.com]visualcrossing.com[/link] and copy your API key.",
                    "Paste it into [set]Weather ApiKey[/set] and select [b]Visual Crossing[/b] as provider.",
                    "Save."
                ]
            }
        },
        "tides": {
            "lead": "Tide data comes from Stormglass.io. You need a free API key from Stormglass and enter it in the watchface settings.",
            "setup": {
                "title": "Set up",
                "steps": [
                    "Create a free account at [link=https://stormglass.io]stormglass.io[/link] and copy your API key.",
                    "Paste it into the Stormglass field in the watchface settings, for example [set]StormglassIO ApiKey[/set], and tap [ui]Save[/ui].",
                    "Wait up to 30 minutes. The first download can take that long."
                ],
                "copyWarn": "The key has more than 60 characters. When copying on a phone, check that nothing is cut off. Some phones stop at a hyphen."
            },
            "plan": {
                "title": "Free plan",
                "text": "The free Stormglass plan allows 10 requests per day. That is enough for tide data. If you also use Stormglass as weather provider (Instinct Mission), you may need a larger plan."
            },
            "coords": {
                "title": "Alternative coordinates",
                "text": "Tides are calculated for the station closest to your position. If you live inland or the wrong station is used, enter the coordinates of a place on the coast in [set]Tide data alternative coordinates (x.xxxxxx,y.yyyyyy)[/set], for example [code]53.551086,9.993682[/code]. This is a PRO option and available in Io, Victus, Zenith, Io Zen, Tactical Elite, Senator, Tomahawk, Vanguard, Black Hawk, Mission Zero and Firmament."
            },
            "download": {
                "title": "Download and offline",
                "text": "The watchface downloads tide data for several days ahead and refreshes it daily when the phone is connected. Newer watchfaces load about 7 days, so the data keeps working for a while without a connection."
            },
            "requirementsTitle": "Requirements",
            "requirements": [
                "The watch is connected to the phone via Bluetooth, and the [b]Garmin Connect[/b] app runs at least in the background.",
                "A position. If the watch shows [watch]GPS?[/watch], [link=/guides/location]do a GPS fix[/link]. After a longer trip, do a GPS fix so the tides are loaded for the new place."
            ],
            "trouble": {
                "noData": {
                    "q": "No tide data appears",
                    "a": "Check that the key was copied completely, wait 30 minutes after saving and do a GPS fix. If you are far from the coast, enter alternative coordinates."
                }
            }
        },
        "location": {
            "lead": "Sunrise, sunset, moonrise, weather and tides depend on where you are. The watchface stores a position and uses it for all of these.",
            "gpsFix": {
                "title": "Do a GPS fix",
                "intro": "If the watch shows [watch]GPS?[/watch] or the place is wrong, the stored position needs an update:",
                "steps": [
                    "Open the start screen of an activity with GPS, for example [ui]Run[/ui]. You do not need to start the activity.",
                    "Wait until the watch has a GPS signal (the GPS indicator turns green).",
                    "Go back to the watchface. The data updates within a few minutes."
                ],
                "moved": "The stored position only changes when you have moved more than about 1 km."
            },
            "phone": {
                "title": "Without a GPS fix",
                "text": "With Garmin weather, the watchface can also take the position of the Garmin weather station. That is often an airport and only an approximate location. For exact sun and moon times, do a GPS fix."
            },
            "check": {
                "title": "Check the position",
                "text": "Newer watchfaces show [watch]Position: Valid[/watch] on the [link=/guides/find-watchface-id]Info page on the watch[/link] when a position is stored."
            },
            "requirementsTitle": "Requirements",
            "requirements": [
                "The watch is connected to the phone via Bluetooth.",
                "The [b]Garmin Connect[/b] app (not Connect IQ) runs at least in the background."
            ],
            "moonNote": "The moon phase is the same everywhere. Moonrise and moonset depend on your position."
        },
        "timezones": {
            "lead": "Many watchfaces can show a second and third time zone. You enter the difference to UTC in minutes.",
            "basics": {
                "title": "Set up the time zones",
                "steps": [
                    "In the watchface settings, choose [b]Timezone 2[/b] or [b]Timezone 3[/b] for one of the data fields.",
                    "Enter both offsets in the field for the time zone offsets: first the second time zone, then the third, separated by a space.",
                    "Save."
                ],
                "format": "Offsets are in minutes relative to UTC. Enter positive values without a plus sign. Multiply hours by 60.",
                "labelIntro": "The field is called, for example:",
                "labelValue": "UTC 2nd/3rd timezone offset in minutes, separate with whitespace (letters behind offsets: A -12h format, B -24h format, C -military format, empty -default)"
            },
            "example": {
                "title": "Example",
                "value": "-300 540",
                "text": "Second time zone New York (UTC−5 → −300), third time zone Tokyo (UTC+9 → 540).",
                "changeText": "To change the second time zone to UTC+6, the first value becomes 360:",
                "changeValue": "360 540"
            },
            "dst": {
                "title": "Daylight saving time",
                "text": "The offsets are fixed. They do not follow daylight saving time. Adjust them when the clocks change, for example New York from −300 (winter) to −240 (summer)."
            },
            "suffix": {
                "title": "12 h, 24 h or military format (A/B/C)",
                "intro": "Some watchfaces let you add a letter directly after an offset, without a space:",
                "rows": [
                    {
                        "term": "A",
                        "text": "12-hour format, shown like [watch]5:30P[/watch]"
                    },
                    {
                        "term": "B",
                        "text": "24-hour format"
                    },
                    {
                        "term": "C",
                        "text": "military format"
                    },
                    {
                        "term": "no letter",
                        "text": "same as the time format of the watch"
                    }
                ],
                "example": "-300A 540B",
                "exampleText": "New York in 12-hour format, Tokyo in 24-hour format.",
                "support": "The letters work in Io, Victus, Zenith, Trio and all newer watchfaces. Horizon, Centurion, Morphius and Dinstinct ignore them. A leading [watch]'[/watch] in front of the time means the other zone is on a different day.",
                "previewTitle": "Live preview",
                "zone2": "2nd zone",
                "zone3": "3rd zone"
            },
            "table": {
                "title": "UTC offsets of selected cities (standard time, minutes)",
                "city": "City",
                "offset": "Offset"
            }
        },
        "themes": {
            "lead": "The theme string stores all color settings of a watchface in one short code. With it you can save, share and switch color setups.",
            "what": {
                "title": "How theme strings work",
                "steps": [
                    "Open the watchface settings. The theme string is in a field near the top, for example [set]Color Theme String[/set].",
                    "Change colors and tap [ui]Save[/ui]. The next time you open the settings, the theme string shows your new colors.",
                    "Copy the theme string to keep your color setup or to share it, for example in a review or a forum.",
                    "To apply a theme string, paste it into the field and tap [ui]Save[/ui]. All colors change at once."
                ],
                "sameFace": "A theme string only works in the watchface it comes from. Every position in the code stands for one color setting of that watchface.",
                "fromSite": "Ready-made themes are on each watchface’s page under [b]Themes[/b]. Tap [ui]Copy[/ui] and paste the string into the settings."
            },
            "sunset": {
                "title": "Switch the theme after sunset",
                "text": "Put an exclamation mark in front of a theme string, for example [code]!AAXXABPBBBFFFF…[/code], and save. This theme is used from sunset to sunrise.",
                "remove": "To turn it off, enter a single [code]![/code] and save.",
                "hidden": "The night theme is not shown in the settings after saving. Keep a copy if you want to change it later."
            },
            "customColors": {
                "title": "Custom colors",
                "text": "Many watchfaces accept up to three colors of your own as 6-digit hex values, for example [code]FF8800[/code], in [set]Set Custom Colors[/set]. They then appear as [b]Custom Color 1–3[/b] in the color lists (PRO)."
            },
            "missingField": "If your settings have no theme string field, theme strings are not supported for this watchface on your watch model."
        },
        "dateFormat": {
            "lead": "In many watchfaces you decide how the date is shown. You enter a pattern of letters, and the watch replaces each letter with the current value.",
            "where": {
                "title": "Where to set it",
                "steps": [
                    "Choose [b]Date 1[/b], [b]Date 2[/b] or [b]Date 3[/b] for one of the data fields, for example [set]📅 Date 1 [set below in watchface settings][/set].",
                    "Further down in the settings, enter the pattern in the matching date format field.",
                    "Save."
                ],
                "dropdown": "Centurion, Morphius and Dinstinct use a list of ready-made formats instead of a text field. Date 3 exists in Horizon, Instinct Mission, Vega, Centurion and Dinstinct."
            },
            "codes": {
                "title": "Format codes",
                "intro": "These letters are replaced. All other characters, such as spaces, dots or slashes, appear as you type them. The code letters cannot be shown as text.",
                "rows": [
                    {
                        "term": "d",
                        "text": "day of the month with leading zero (07)"
                    },
                    {
                        "term": "n",
                        "text": "day of the month without leading zero (7)"
                    },
                    {
                        "term": "D",
                        "text": "weekday, short (Mon)"
                    },
                    {
                        "term": "m",
                        "text": "month as number with leading zero (02)"
                    },
                    {
                        "term": "o",
                        "text": "month as number without leading zero (2)"
                    },
                    {
                        "term": "M",
                        "text": "month, short (Feb)"
                    },
                    {
                        "term": "y",
                        "text": "year, two digits (26)"
                    },
                    {
                        "term": "Y",
                        "text": "year, four digits (2026)"
                    },
                    {
                        "term": "w",
                        "text": "week number"
                    },
                    {
                        "term": "j",
                        "text": "Julian date: two-digit year + day of the year (2658)"
                    },
                    {
                        "term": "p",
                        "text": "day of the year"
                    }
                ]
            },
            "examples": {
                "title": "Examples",
                "rows": [
                    {
                        "term": "D, d.m.Y",
                        "text": "Fri, 27.02.2026"
                    },
                    {
                        "term": "d M y",
                        "text": "27 Feb 26"
                    },
                    {
                        "term": "Y-m-d",
                        "text": "2026-02-27"
                    },
                    {
                        "term": "D n/o",
                        "text": "Fri 27/2"
                    }
                ]
            },
            "preview": {
                "title": "Try it",
                "label": "Pattern",
                "result": "Shown on the watch"
            }
        },
        "fonts": {
            "lead": "Overview of the time and data fonts. Which fonts are offered and what they are called depends on the watchface. The list in the watchface settings is what counts."
        },
        "switchviews": {
            "alternate": {
                "title": "Second data field set",
                "text": "Fletcher and Night Fever have a second set of six data fields, set with [set]Alternate data field: Top Option[/set] and the following fields. Switch on [set]Activate second wrist gesture for alternate data field set[/set] to swap the sets with a second wrist raise within 7 seconds. You can also assign [b]Switch datafield set[/b] to the hour or minute long press.\n\nSenator has a second set for all eight data fields, marked (2nd set). Select [b]Dataset 2 Top[/b] or [b]Dataset 2 Bottom[/b] as a switchview to show it.\n\nTactical Elite shows six extra fields and an extra bar with the switchview [b]6 extra datafields + bar[/b]."
            },
            "lead": "Some areas of a watchface can show a second view. You switch with a second wrist movement or, on touch watches, with a long press.",
            "basics": {
                "title": "What are switchviews?",
                "text": "Instead of one fixed content, an area shows view 1 or view 2. You set both in the watchface settings, for example [set]Data Top Half 2 (Switchview)[/set] or [set]Switchview 2 (switches on second wrist move)[/set]. The names differ slightly per watchface.",
                "pro": "View 2 is a PRO option. It works during the trial and after unlocking."
            },
            "gesture": {
                "title": "Switch with a second wrist movement",
                "steps": [
                    "Raise your wrist to wake the watch.",
                    "Lower your wrist.",
                    "Raise it again within about 7 seconds. The watchface shows the other view."
                ],
                "setting": "In newer watchfaces the gesture can be turned on and off with [set]Toggle Switchview 1/2 by second wrist gesture also[/set]. It is on by default on MIP watches and off on AMOLED watches.",
                "notTooFast": "Move the wrist clearly but not too abruptly."
            },
            "touch": {
                "title": "Switch with a long press (touch watches)",
                "text": "On touch watches with Connect IQ 4.2 or later, newer watchfaces let you assign actions to a long press on the hour, the minute or the icons in the middle: [set]Hour Long Press Action[/set], [set]Minute Long Press Action[/set] and similar.",
                "defaults": "In Senator, Tomahawk, Vanguard, Black Hawk and Mission Zero the hour toggles the top view and the minute the bottom view by default.",
                "moreLink": "All long-press options are explained on the watchface pages."
            },
            "stop": {
                "title": "Stop switching",
                "text": "If the display keeps changing and you do not want that, set view 2 to [b]Do not switch[/b] or to the same content as view 1."
            },
            "trouble": {
                "nothing": {
                    "q": "Nothing changes",
                    "items": [
                        "Check that a view 2 is set and is not [b]Do not switch[/b].",
                        "Check that the gesture is turned on with [set]Toggle Switchview 1/2 by second wrist gesture also[/set]. On AMOLED watches it is off by default.",
                        "After the trial, switchviews only work after unlocking.",
                        "On older watches (fenix 5/6, Forerunner 245/945, vívoactive 3/4) some newer watchfaces do not offer a view 2."
                    ]
                }
            }
        }
    }
};
export default guides;
